import React from "react"
import { Grid } from "@material-ui/core"

import { ActionDialog } from "@/components"
import MessageItem from "@/components/ChatMessageBuilder/MessageItem"
import { Variable } from "@/components/ChatMessageBuilder/VariableInput"

import {
	WABAChannelMessageTemplate,
	WABATemplateVariableIndexToVariableName,
	WABATemplateVariableMapping
} from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import WABATemplateVariableMappingDialog from "@/@integrations/WABA/components/Chat/WABAMessageTemplate/WABATemplateVariableMappingDialog"
import WABAChannelMessageTemplateService from "@/@integrations/WABA/services/WABAChannelMessageTemplate"

import ApiService from "@/services/Api"
import { regexList } from "@/utils/regex"
import { useGlobalStateStore } from "@/store/GlobalState"
import { renderComponent } from "@/utils/node"

type UseWABATemplateVariableMappingOptions = {
	inboxChannelId: number
	onSelect: (wabaChannelMessageTemplate: WABAChannelMessageTemplate) => Promise<void>
	onAfterSave?: () => void
	dialog: {
		/**
		 * When true, a confirmation dialog is shown even for templates without unmapped variables.
		 * Use true in send contexts (inbox chat). Use false in configuration contexts (flow builder)
		 * where selecting an already-mapped template should proceed immediately without a dialog.
		 */
		requireConfirmationWhenMapped: boolean
		title: (templateName: string) => string
		saveText: string
	}
}

const useWABATemplateVariableMapping = (options: UseWABATemplateVariableMappingOptions) => {
	const { inboxChannelId, onSelect, onAfterSave, dialog } = options

	const globalStateStore = useGlobalStateStore()

	/**
	 * A variable needs manual mapping only when it is numeric (e.g. "1", "2") AND its mapped
	 * value is still the numeric fallback (value === key). Named variables (e.g. "primeiro_nome")
	 * created via the Meta "Name" type are already valid Letalk variable names and resolve
	 * automatically against the contact's data, no manual mapping needed.
	 */
	const isUnmappedVariable = (metaVariable: string, mappedVariable: string) =>
		regexList.is_only_digits.test(metaVariable) && metaVariable === mappedVariable

	const extractUnmappedVariableIndicesBySection = (variableMapping?: WABATemplateVariableMapping): Record<keyof WABATemplateVariableMapping, string[]> => {
		const extractFromSection = (section?: WABATemplateVariableIndexToVariableName): string[] => {
			return Object.entries(section || {})
				.filter(([metaVariable, mappedVariable]) => isUnmappedVariable(metaVariable, mappedVariable))
				.map(([metaVariable]) => metaVariable)
		}

		return {
			bodyText: extractFromSection(variableMapping?.bodyText),
			bodyHeaderText: extractFromSection(variableMapping?.bodyHeaderText),
			buttonUrl: extractFromSection(variableMapping?.buttonUrl)
		}
	}

	const handleSelectTemplate = async (wabaChannelMessageTemplate: WABAChannelMessageTemplate) => {
		const sectionToUnmappedVariableIndex = extractUnmappedVariableIndicesBySection(wabaChannelMessageTemplate.variableMapping)

		const allUnmappedIndices = [
			...sectionToUnmappedVariableIndex.bodyText,
			...sectionToUnmappedVariableIndex.bodyHeaderText,
			...sectionToUnmappedVariableIndex.buttonUrl
		]
		const hasUnmappedVariables = allUnmappedIndices.length > 0

		if (!hasUnmappedVariables && !dialog.requireConfirmationWhenMapped) {
			await onSelect(wabaChannelMessageTemplate)
			onAfterSave?.()
			return
		}

		let availableVariables: Variable[] = []

		if (hasUnmappedVariables) {
			const response = await ApiService.get<Variable[]>(`/channels/${globalStateStore.currentChannel?.integrationId}/all/variables`)
			availableVariables = response.data
		}

		const handleSaveMapping = async (selectedVariableByIndex: WABATemplateVariableIndexToVariableName) => {
			if (hasUnmappedVariables) {
				const updatedVariableMapping: WABATemplateVariableMapping = {
					bodyText: {
						...wabaChannelMessageTemplate.variableMapping?.bodyText,
						...Object.fromEntries(
							sectionToUnmappedVariableIndex.bodyText.map(variableIndex => [variableIndex, selectedVariableByIndex[variableIndex] ?? variableIndex])
						)
					},
					bodyHeaderText: {
						...wabaChannelMessageTemplate.variableMapping?.bodyHeaderText,
						...Object.fromEntries(
							sectionToUnmappedVariableIndex.bodyHeaderText.map(variableIndex => [variableIndex, selectedVariableByIndex[variableIndex] ?? variableIndex])
						)
					},
					buttonUrl: {
						...wabaChannelMessageTemplate.variableMapping?.buttonUrl,
						...Object.fromEntries(
							sectionToUnmappedVariableIndex.buttonUrl.map(variableIndex => [variableIndex, selectedVariableByIndex[variableIndex] ?? variableIndex])
						)
					}
				}

				await WABAChannelMessageTemplateService.updateVariableMapping(
					wabaChannelMessageTemplate.id,
					inboxChannelId,
					updatedVariableMapping
				)

				await onSelect({
					...wabaChannelMessageTemplate,
					variableMapping: updatedVariableMapping
				})
			} else {
				await onSelect(wabaChannelMessageTemplate)
			}

			onAfterSave?.()
		}

		if (hasUnmappedVariables) {
			renderComponent(
				"waba-template-variable-mapping-dialog",
				<WABATemplateVariableMappingDialog
					template={wabaChannelMessageTemplate}
					unmappedIndices={allUnmappedIndices}
					unmappedBySection={sectionToUnmappedVariableIndex}
					availableVariables={availableVariables}
					title={dialog.title(wabaChannelMessageTemplate.name)}
					saveText={dialog.saveText}
					onSave={handleSaveMapping}
					onClose={onAfterSave}
				/>
			)
		} else {
			ActionDialog.open({
				title: dialog.title(wabaChannelMessageTemplate.name),
				cancelText: "CANCELAR",
				saveText: dialog.saveText,
				openDialog: true,
				forcedRender: true,
				fullWidth: true,
				children: (
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Grid container>
								<MessageItem
									copySpecial={false}
									type={wabaChannelMessageTemplate.message.type}
									content={wabaChannelMessageTemplate.message.content}
									mediaName={wabaChannelMessageTemplate.message.mediaName}
									extraData={wabaChannelMessageTemplate.message.extraData}
									style={{ width: "100%" }}
								/>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							Tem certeza que deseja enviar o template acima?
						</Grid>
					</Grid>
				),
				onSave: () => handleSaveMapping({})
			})
		}
	}

	return { handleSelectTemplate }
}

export default useWABATemplateVariableMapping
